import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Drawer, DrawerContent, DrawerTrigger } from "@/components/ui/drawer";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useDebounce } from "@/hooks/useDebounce";
import useScreen from "@/hooks/useScreen";
import { cn } from "@/lib/utils";
import { Option } from "@/types";
import { Check, ChevronsUpDown, Search, X } from "lucide-react";
import React, { SetStateAction, useEffect, useRef, useState } from "react";
import SkeletonRow from "./SkeletonRow";

// --- 1. Sub-komponen ---

const ServerSearchInput = ({
  className,
  value,
  onChange,
}: {
  className?: string;
  value: string;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
}) => (
  <div
    className={cn(
      "flex items-center border-b border-slate-200 dark:border-slate-800 px-3",
      className,
    )}
  >
    <Search className="mr-2 h-4 w-4 shrink-0 text-slate-500 dark:text-slate-400" />
    <input
      value={value}
      onChange={onChange}
      placeholder="Search..."
      className={cn(
        "flex h-11 w-full rounded-md bg-transparent py-3 text-sm outline-none disabled:cursor-not-allowed disabled:opacity-50",
        "text-slate-900 dark:text-slate-100", // Text color
        "placeholder:text-slate-400 dark:placeholder:text-slate-500", // Placeholder color
        "text-base md:text-sm", // Mencegah zoom di iOS
      )}
    />
  </div>
);

const OptionListContent = ({
  isPending,
  options,
  value,
  setValue,
  setOpen,
}: {
  isPending?: boolean;
  options: Array<Option>;
  value: string;
  setValue?: (newValue: string) => void;
  setOpen: (open: boolean) => void;
}) => (
  <CommandList className="bg-white dark:bg-slate-950">
    {isPending ? (
      <div className="p-4 space-y-2">
        {Array(3)
          .fill(null)
          .map((_, index) => (
            <SkeletonRow key={index} />
          ))}
      </div>
    ) : (
      <>
        <CommandEmpty className="py-6 text-center text-sm text-slate-500 dark:text-slate-400">
          Tidak ada data ditemukan.
        </CommandEmpty>
        <CommandGroup>
          {options?.length > 0 &&
            options?.map((item, index) => (
              <CommandItem
                key={index}
                value={item.label}
                onSelect={() => {
                  if (setValue) {
                    setValue(
                      String(item.value) === String(value)
                        ? ""
                        : String(item.value),
                    );
                  }
                  setOpen(false);
                }}
                className="cursor-pointer dark:text-slate-200 dark:aria-selected:bg-slate-800 dark:aria-selected:text-slate-50"
              >
                <Check
                  className={cn(
                    "mr-2 h-4 w-4",
                    String(value) === String(item.value)
                      ? "opacity-100 text-primary"
                      : "opacity-0",
                  )}
                />
                {item.label}
              </CommandItem>
            ))}
        </CommandGroup>
      </>
    )}
  </CommandList>
);

// --- 2. Komponen Utama ---

export default function Combobox({
  open,
  setOpen,
  options = [],
  value,
  setValue,
  placeholder,
  serverSide = false,
  onChange,
  isPending,
  searchQueryValue = "",
  disabled = false,
  delay = 500,
}: {
  open: boolean;
  setOpen: React.Dispatch<SetStateAction<boolean>>;
  options: Array<Option>;
  value: string;
  setValue?: (newValue: string) => void;
  placeholder: string;
  serverSide?: boolean;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  isPending?: boolean;
  searchQueryValue?: string;
  disabled?: boolean;
  delay?: number;
}) {
  const { isDesktop } = useScreen();
  const selected = options?.find((item) => String(item.value) == String(value));

  const isMounted = useRef(false);
  const [inputValue, setInputValue] = useState(searchQueryValue);
  const debouncedSearchQuery = useDebounce(inputValue, delay);

  useEffect(() => {
    if (searchQueryValue !== inputValue) {
      setInputValue(searchQueryValue);
    }
  }, [searchQueryValue]);

  useEffect(() => {
    if (serverSide && onChange) {
      if (!isMounted.current) {
        isMounted.current = true;
        return;
      }
      if (debouncedSearchQuery === searchQueryValue) return;

      const dummyEvent = {
        target: { value: debouncedSearchQuery },
      } as React.ChangeEvent<HTMLInputElement>;

      onChange(dummyEvent);
    }
  }, [debouncedSearchQuery]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
  };

  // Fungsi rendering konten Trigger
  const renderButtonContent = () => (
    <>
      <div className="flex flex-1 truncate items-center justify-start text-left">
        {value && selected ? (
          <span className="truncate text-slate-900 dark:text-slate-100">
            {selected.label}
          </span>
        ) : (
          <span className="truncate text-slate-500 dark:text-slate-400 font-normal">
            {placeholder}
          </span>
        )}
      </div>
      <div className="flex items-center gap-1">
        {value && selected && setValue && !disabled && (
          <div
            role="button"
            className="p-1 rounded-md hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors group"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setValue("");
            }}
          >
            <X className="h-3.5 w-3.5 shrink-0 text-slate-400 group-hover:text-slate-600 dark:text-slate-500 dark:group-hover:text-slate-300" />
          </div>
        )}
        <ChevronsUpDown className="h-4 w-4 shrink-0 text-slate-400 dark:text-slate-500" />
      </div>
    </>
  );

  // Style untuk trigger button (dipisahkan agar konsisten Desktop & Mobile)
  const triggerBtnStyles = cn(
    "w-full justify-between px-3 border shadow-sm transition-colors",
    "bg-white border-slate-200 hover:bg-slate-50 hover:border-slate-300", // Light Mode
    "dark:bg-slate-900 dark:border-slate-800 dark:hover:bg-slate-800/80 dark:hover:border-slate-700", // Dark Mode
    disabled &&
      "opacity-50 cursor-not-allowed pointer-events-none bg-slate-50 dark:bg-slate-950",
  );

  // Render untuk Desktop
  if (isDesktop) {
    return (
      <Popover open={open && !disabled} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className={triggerBtnStyles}
            disabled={disabled}
          >
            {renderButtonContent()}
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className="w-[--radix-popover-trigger-width] min-w-[200px] p-0 border-slate-200 dark:border-slate-800 shadow-md dark:shadow-xl dark:shadow-black/50"
          align="start"
        >
          <Command
            shouldFilter={!serverSide}
            className="bg-white dark:bg-slate-950"
          >
            {serverSide ? (
              <ServerSearchInput
                value={inputValue}
                onChange={handleInputChange}
              />
            ) : (
              <CommandInput
                placeholder="Search..."
                className="dark:text-slate-100 placeholder:dark:text-slate-500 border-none focus:ring-0"
              />
            )}
            <OptionListContent
              isPending={isPending}
              options={options}
              value={value}
              setValue={setValue}
              setOpen={setOpen}
            />
          </Command>
        </PopoverContent>
      </Popover>
    );
  }

  // Render untuk Mobile (Drawer)
  return (
    <Drawer open={open && !disabled} onOpenChange={setOpen}>
      <DrawerTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={triggerBtnStyles}
          disabled={disabled}
        >
          {renderButtonContent()}
        </Button>
      </DrawerTrigger>
      <DrawerContent className="bg-white dark:bg-slate-950 border-t-slate-200 dark:border-t-slate-800">
        <div className="mt-4 border-t border-slate-200 dark:border-slate-800">
          <Command
            className="h-[50vh] bg-transparent"
            shouldFilter={!serverSide}
          >
            {serverSide ? (
              <ServerSearchInput
                value={inputValue}
                onChange={handleInputChange}
              />
            ) : (
              <CommandInput
                placeholder="Search..."
                className="text-base dark:text-slate-100 placeholder:dark:text-slate-500 border-none"
              />
            )}
            <OptionListContent
              isPending={isPending}
              options={options}
              value={value}
              setValue={setValue}
              setOpen={setOpen}
            />
          </Command>
        </div>
      </DrawerContent>
    </Drawer>
  );
}
